import React, { useState, useEffect, useRef } from "react";
import { Box, Pagination, Stack, Typography, Select, MenuItem, FormControl, InputLabel, Button, IconButton } from "@mui/material";
import { ChevronLeft, ChevronRight, FirstPage, LastPage } from "@mui/icons-material";
import { useGetSellerCampaigns, useCampaignMetrics } from "../../client/hooks/products/promotions.queries";
import { useExportCampaignCandidates, useCheckExportStatus, useDownloadExport } from "../../client/hooks/products/promotions.mutations";
import CustomSnackbar from "../components/Message.Component";
import CampaignHeader from "./components/campaigns/CampaignHeader";
import CampaignStats from "./components/campaigns/CampaignStats";
import CampaignViewManager from "./components/campaigns/CampaignViewManager";
import CampaignDetails from "./components/campaigns/CampaignDetails";
import AderirEmMassa from "./components/campaigns/AderirEmMassa";
import TriggerManagementModal from "./components/campaigns/TriggerManagementModal";
import ExportHistoryModal from "./components/campaigns/ExportHistoryModal";

export default function CampanhasPage() {
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [bulkJoinOpen, setBulkJoinOpen] = useState(false);
    const [triggersModalOpen, setTriggersModalOpen] = useState(false);
    const [exportHistoryOpen, setExportHistoryOpen] = useState(false);
    const [alert, setAlert] = useState(false);
    const [severity, setSeverity] = useState("info");
    const [message, setMessage] = useState("");
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    
    const [exportStatus, setExportStatus] = useState('idle');
    const [currentJobId, setCurrentJobId] = useState(null);
    const pollingRef = useRef(null);

    const {
        data: campaignsData = { results: [], paging: { total: 0, limit: 20, offset: 0 } },
        isLoading,
        error,
        refetch
    } = useGetSellerCampaigns({ page, limit }, true);

    const {
        data: metricsData,
        isLoading: metricsLoading,
        error: metricsError,
        refetch: refetchMetrics
    } = useCampaignMetrics(true);

    const exportCandidatesMutation = useExportCampaignCandidates();
    const checkStatusMutation = useCheckExportStatus();
    const downloadMutation = useDownloadExport();

    const campaigns = {
        results: campaignsData.results || [],
        total: campaignsData.paging?.total || 0
    };

    const clearPolling = () => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    };

    const startPolling = (jobId) => {
        clearPolling();
        pollingRef.current = setInterval(async () => {
            try {
                const statusResponse = await checkStatusMutation.mutateAsync({ jobId });
                
                if (statusResponse.status === 'completed') {
                    clearPolling();
                    setExportStatus('downloading');
                    
                    try {
                        const fileBlob = await downloadMutation.mutateAsync({ jobId });
                        
                        const url = window.URL.createObjectURL(fileBlob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `candidatos-campanhas-${new Date().toISOString().split('T')[0]}.xls`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                        
                        setExportStatus('idle');
                        setCurrentJobId(null);
                        showAlert("Arquivo exportado com sucesso!", "success");
                        
                    } catch (error) {
                        setExportStatus('idle');
                        setCurrentJobId(null);
                        showAlert("Erro ao baixar o arquivo", "error");
                    }
                    
                } else if (statusResponse.status === 'failed') {
                    clearPolling();
                    setExportStatus('idle');
                    setCurrentJobId(null);
                    showAlert("Falha na exportação", "error");
                }
                
            } catch (error) {
                clearPolling();
                setExportStatus('idle');
                setCurrentJobId(null);
                showAlert("Erro ao verificar status da exportação", "error");
            }
        }, 3000);
    };

    useEffect(() => {
        return () => clearPolling();
    }, []);

    const showAlert = (msg, sev = "info") => {
        setMessage(msg);
        setSeverity(sev);
        setAlert(true);
    };

    const handleViewDetails = (campaign) => {
        setSelectedCampaign(campaign);
        setDetailsOpen(true);
    };

    const handleCampaignAction = (action, campaign) => {
        switch (action) {
            case 'activate':
                showAlert("Campanha ativada", "success");
                break;
            case 'pause':
                showAlert("Campanha pausada", "info");
                break;
            case 'delete':
                showAlert("Campanha excluída", "warning");
                break;
            case 'duplicate':
                showAlert("Campanha duplicada com sucesso", "success");
                break;
            case 'export':
                showAlert("Exportação iniciada", "info");
                break;
            default:
                showAlert("Ação executada com sucesso", "success");
        }
        refetch();
        refetchMetrics();
    };

    const handleOpenExportHistory = () => {
        setExportHistoryOpen(true);
    };

    const handleBulkJoin = () => {
        setBulkJoinOpen(true);
    };

    const handleExportCandidates = async () => {
        if (exportStatus !== 'idle') return;
        
        setExportStatus('processing');
        
        try {
            const response = await exportCandidatesMutation.mutateAsync({ 
                data: {} 
            });
            
            if (response.jobId) {
                setCurrentJobId(response.jobId);
                showAlert("Exportação iniciada. Aguarde o processamento...", "info");
                startPolling(response.jobId);
            } else {
                setExportStatus('idle');
                showAlert("Erro: JobId não retornado", "error");
            }
            
        } catch (error) {
            setExportStatus('idle');
            showAlert("Erro ao iniciar exportação", "error");
        }
    };

    const handleBulkJoinSuccess = (results) => {
        const successful = results.filter(r => r.success).length;
        const failed = results.filter(r => !r.success).length;
        const totalItems = results.reduce((sum, r) => sum + r.itemCount, 0);

        if (failed === 0) {
            showAlert(`Sucesso! ${successful} campanhas processadas com ${totalItems} produtos adicionados.`, "success");
        } else {
            showAlert(`Processamento concluído. ${successful} campanhas bem-sucedidas, ${failed} com erro. Total: ${totalItems} produtos.`, "warning");
        }
        
        refetch();
        refetchMetrics();
        setBulkJoinOpen(false);
    };
    
    const handleManageCampaign = () => {
        showAlert("Funcionalidade de gestão de campanhas em desenvolvimento", "info");
    };

    const handleManageTriggers = () => {
        setTriggersModalOpen(true);
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleLimitChange = (event) => {
        const newLimit = event.target.value;
        setLimit(newLimit);
        setPage(1); // Reset to first page when changing limit
    };

    const totalPages = Math.ceil((campaignsData.paging?.total || 0) / limit);

    return (
        <Box sx={{
            p: 3,
            bgcolor: '#f5f5f5',
            minHeight: '100vh'
        }}>
            <CampaignHeader
                onRefresh={() => { refetch(); refetchMetrics(); }}
                onOpenExportHistory={handleOpenExportHistory}
                onBulkJoin={handleBulkJoin}
                onExportCandidates={handleExportCandidates}
                onManageTriggers={handleManageTriggers}
                isLoading={isLoading}
                campaignCount={campaigns?.results?.length || 0}
                exportStatus={exportStatus}
            />
            <CampaignStats campaigns={campaigns} metricsData={metricsData} metricsLoading={metricsLoading} />
            <CampaignViewManager
                campaigns={campaigns}
                isLoading={isLoading}
                error={error}
                onViewDetails={handleViewDetails}
                onEdit={handleCampaignAction}
            />

            {!isLoading && campaigns?.results?.length > 0 && (
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mt: 4,
                    mb: 2,
                    flexWrap: 'wrap',
                    gap: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Itens por página</InputLabel>
                            <Select
                                value={limit}
                                label="Itens por página"
                                onChange={handleLimitChange}
                                sx={{
                                    '& .MuiSelect-select': {
                                        py: 1
                                    }
                                }}
                            >
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={20}>20</MenuItem>
                                <MenuItem value={30}>30</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                            </Select>
                        </FormControl>
                        <Typography variant="body2" color="text.secondary">
                            Mostrando {((page - 1) * limit) + 1} a {Math.min(page * limit, campaignsData.paging?.total || 0)} de {campaignsData.paging?.total || 0} campanhas
                        </Typography>
                    </Box>

                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 'max-content' }}>
                                Página {page} de {totalPages}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <IconButton
                                    onClick={() => handlePageChange(null, 1)}
                                    disabled={page === 1}
                                    size="small"
                                    sx={{
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 2,
                                        '&:hover': {
                                            bgcolor: '#f5f5f5',
                                            borderColor: '#3483fa'
                                        },
                                        '&.Mui-disabled': {
                                            borderColor: '#e0e0e0',
                                            color: '#bbb'
                                        }
                                    }}
                                >
                                    <FirstPage fontSize="small" />
                                </IconButton>
                                
                                <Button
                                    onClick={() => handlePageChange(null, page - 1)}
                                    disabled={page === 1}
                                    startIcon={<ChevronLeft />}
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        borderColor: '#e0e0e0',
                                        color: '#666',
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        minWidth: 100,
                                        '&:hover': {
                                            bgcolor: '#f5f5f5',
                                            borderColor: '#3483fa',
                                            color: '#3483fa'
                                        },
                                        '&.Mui-disabled': {
                                            borderColor: '#e0e0e0',
                                            color: '#bbb'
                                        }
                                    }}
                                >
                                    Anterior
                                </Button>

                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={handlePageChange}
                                    color="primary"
                                    size="medium"
                                    siblingCount={1}
                                    boundaryCount={1}
                                    hidePrevButton
                                    hideNextButton
                                    sx={{
                                        '& .MuiPaginationItem-root': {
                                            borderRadius: 2,
                                            fontWeight: 600,
                                            border: '1px solid #e0e0e0',
                                            '&:hover': {
                                                bgcolor: '#f5f5f5',
                                                borderColor: '#3483fa'
                                            }
                                        },
                                        '& .Mui-selected': {
                                            bgcolor: '#3483fa !important',
                                            color: 'white',
                                            borderColor: '#3483fa !important'
                                        }
                                    }}
                                />

                                <Button
                                    onClick={() => handlePageChange(null, page + 1)}
                                    disabled={page === totalPages}
                                    endIcon={<ChevronRight />}
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        borderColor: '#e0e0e0',
                                        color: '#666',
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        minWidth: 100,
                                        '&:hover': {
                                            bgcolor: '#f5f5f5',
                                            borderColor: '#3483fa',
                                            color: '#3483fa'
                                        },
                                        '&.Mui-disabled': {
                                            borderColor: '#e0e0e0',
                                            color: '#bbb'
                                        }
                                    }}
                                >
                                    Próximo
                                </Button>
                                
                                <IconButton
                                    onClick={() => handlePageChange(null, totalPages)}
                                    disabled={page === totalPages}
                                    size="small"
                                    sx={{
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 2,
                                        '&:hover': {
                                            bgcolor: '#f5f5f5',
                                            borderColor: '#3483fa'
                                        },
                                        '&.Mui-disabled': {
                                            borderColor: '#e0e0e0',
                                            color: '#bbb'
                                        }
                                    }}
                                >
                                    <LastPage fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                    )}
                </Box>
            )}

            <CampaignDetails
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                campaign={selectedCampaign}
                onManage={handleManageCampaign}
            />

            <AderirEmMassa
                open={bulkJoinOpen}
                onClose={() => setBulkJoinOpen(false)}
                onSuccess={handleBulkJoinSuccess}
            />

            <TriggerManagementModal
                open={triggersModalOpen}
                onClose={() => setTriggersModalOpen(false)}
            />

            <ExportHistoryModal
                open={exportHistoryOpen}
                onClose={() => setExportHistoryOpen(false)}
                onSuccess={(msg) => showAlert(msg, "success")}
                onError={(msg) => showAlert(msg, "error")}
            />

            <CustomSnackbar
                open={alert}
                onClose={() => setAlert(false)}
                severity={severity}
                message={message}
            />
        </Box>
    );
}
